/**
 * Self-contained QR Code (Model 2) byte-mode encoder — no third-party deps.
 * Supports versions 1..10 with ECC level L or M, which is far more than enough
 * for a ~50-character pairing token. Rendering is done to an inline SVG string.
 *
 * The pairing token is a SECRET, so the QR must be generated entirely locally
 * (never round-tripped through a remote QR service).
 *
 * If anything here fails, callers fall back to the copyable token + SAS, so a
 * QR bug never blocks pairing.
 */

export type EcLevel = "L" | "M";

interface GroupSpec {
  blocks: number;
  dataPerBlock: number;
}
interface VersionEc {
  ecPerBlock: number;
  groups: GroupSpec[];
  totalData: number;
}

// Index 0 => version 1. Values from ISO/IEC 18004 error-correction tables.
const EC_TABLE: Record<EcLevel, VersionEc[]> = {
  L: [
    { ecPerBlock: 7, groups: [{ blocks: 1, dataPerBlock: 19 }], totalData: 19 },
    {
      ecPerBlock: 10,
      groups: [{ blocks: 1, dataPerBlock: 34 }],
      totalData: 34,
    },
    {
      ecPerBlock: 15,
      groups: [{ blocks: 1, dataPerBlock: 55 }],
      totalData: 55,
    },
    {
      ecPerBlock: 20,
      groups: [{ blocks: 1, dataPerBlock: 80 }],
      totalData: 80,
    },
    {
      ecPerBlock: 26,
      groups: [{ blocks: 1, dataPerBlock: 108 }],
      totalData: 108,
    },
    {
      ecPerBlock: 18,
      groups: [{ blocks: 2, dataPerBlock: 68 }],
      totalData: 136,
    },
    {
      ecPerBlock: 20,
      groups: [{ blocks: 2, dataPerBlock: 78 }],
      totalData: 156,
    },
    {
      ecPerBlock: 24,
      groups: [{ blocks: 2, dataPerBlock: 97 }],
      totalData: 194,
    },
    {
      ecPerBlock: 30,
      groups: [{ blocks: 2, dataPerBlock: 116 }],
      totalData: 232,
    },
    {
      ecPerBlock: 18,
      groups: [
        { blocks: 2, dataPerBlock: 68 },
        { blocks: 2, dataPerBlock: 69 },
      ],
      totalData: 274,
    },
  ],
  M: [
    {
      ecPerBlock: 10,
      groups: [{ blocks: 1, dataPerBlock: 16 }],
      totalData: 16,
    },
    {
      ecPerBlock: 16,
      groups: [{ blocks: 1, dataPerBlock: 28 }],
      totalData: 28,
    },
    {
      ecPerBlock: 26,
      groups: [{ blocks: 1, dataPerBlock: 44 }],
      totalData: 44,
    },
    {
      ecPerBlock: 18,
      groups: [{ blocks: 2, dataPerBlock: 32 }],
      totalData: 64,
    },
    {
      ecPerBlock: 24,
      groups: [{ blocks: 2, dataPerBlock: 43 }],
      totalData: 86,
    },
    {
      ecPerBlock: 16,
      groups: [{ blocks: 4, dataPerBlock: 27 }],
      totalData: 108,
    },
    {
      ecPerBlock: 18,
      groups: [{ blocks: 4, dataPerBlock: 31 }],
      totalData: 124,
    },
    {
      ecPerBlock: 22,
      groups: [
        { blocks: 2, dataPerBlock: 38 },
        { blocks: 2, dataPerBlock: 39 },
      ],
      totalData: 154,
    },
    {
      ecPerBlock: 22,
      groups: [
        { blocks: 3, dataPerBlock: 36 },
        { blocks: 2, dataPerBlock: 37 },
      ],
      totalData: 182,
    },
    {
      ecPerBlock: 26,
      groups: [
        { blocks: 4, dataPerBlock: 43 },
        { blocks: 1, dataPerBlock: 44 },
      ],
      totalData: 216,
    },
  ],
};

const ALIGN_POSITIONS: number[][] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

/* ------------------------------- GF(256) --------------------------------- */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
})();

function gmul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

function rsGenerator(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] = next[j]! ^ gmul(poly[j]!, 1); // * x
      next[j + 1] = next[j + 1]! ^ gmul(poly[j]!, EXP[i]!);
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGenerator(ecLen);
  const res = data.concat(new Array<number>(ecLen).fill(0));
  for (let i = 0; i < data.length; i++) {
    const factor = res[i]!;
    if (factor !== 0) {
      for (let j = 0; j < gen.length; j++) {
        res[i + j] = res[i + j]! ^ gmul(gen[j]!, factor);
      }
    }
  }
  return res.slice(data.length);
}

/* --------------------------------- encode -------------------------------- */

export interface QrMatrix {
  size: number;
  modules: boolean[][];
}

export function encodeQr(text: string, ecLevel: EcLevel = "M"): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  const version = chooseVersion(bytes.length, ecLevel);
  const spec = EC_TABLE[ecLevel][version - 1]!;

  const dataCodewords = buildDataCodewords(bytes, version, spec);
  const finalCodewords = interleave(dataCodewords, spec);

  const size = version * 4 + 17;
  const modules = create2d(size, false);
  const isFn = create2d(size, false);

  drawFunctionPatterns(modules, isFn, version, size);
  drawCodewords(modules, isFn, finalCodewords, size);

  const mask = chooseBestMask(modules, isFn, ecLevel, size);
  applyMask(modules, isFn, mask, size);
  drawFormatBits(modules, ecLevel, mask, size);

  return { size, modules };
}

function chooseVersion(byteLen: number, ecLevel: EcLevel): number {
  for (let v = 1; v <= 10; v++) {
    const ccBits = v <= 9 ? 8 : 16;
    const capacityBits = EC_TABLE[ecLevel][v - 1]!.totalData * 8;
    const needed = 4 + ccBits + byteLen * 8;
    if (needed <= capacityBits) return v;
  }
  throw new Error("pairing token too large for QR versions 1-10");
}

function buildDataCodewords(
  bytes: Uint8Array,
  version: number,
  spec: VersionEc
): number[] {
  const bits: number[] = [];
  const put = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1);
  };
  put(0b0100, 4); // byte mode
  put(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) put(b, 8);

  const capacity = spec.totalData * 8;
  // Terminator (up to 4 zero bits).
  for (let i = 0; i < 4 && bits.length < capacity; i++) bits.push(0);
  // Pad to byte boundary.
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!;
    codewords.push(byte);
  }
  // Pad bytes.
  const padBytes = [0xec, 0x11];
  let p = 0;
  while (codewords.length < spec.totalData) {
    codewords.push(padBytes[p % 2]!);
    p++;
  }
  return codewords;
}

function interleave(dataCodewords: number[], spec: VersionEc): number[] {
  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (const g of spec.groups) {
    for (let b = 0; b < g.blocks; b++) {
      const block = dataCodewords.slice(offset, offset + g.dataPerBlock);
      offset += g.dataPerBlock;
      dataBlocks.push(block);
      ecBlocks.push(rsEncode(block, spec.ecPerBlock));
    }
  }
  const result: number[] = [];
  const maxData = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxData; i++) {
    for (const block of dataBlocks)
      if (i < block.length) result.push(block[i]!);
  }
  for (let i = 0; i < spec.ecPerBlock; i++) {
    for (const block of ecBlocks) result.push(block[i]!);
  }
  return result;
}

/* ------------------------------- matrix ---------------------------------- */

function create2d(n: number, fill: boolean): boolean[][] {
  return Array.from({ length: n }, () => new Array<boolean>(n).fill(fill));
}

function drawFunctionPatterns(
  m: boolean[][],
  fn: boolean[][],
  version: number,
  size: number
): void {
  // Timing patterns.
  for (let i = 0; i < size; i++) {
    setFn(m, fn, 6, i, i % 2 === 0);
    setFn(m, fn, i, 6, i % 2 === 0);
  }
  // Finder patterns + separators.
  drawFinder(m, fn, 0, 0);
  drawFinder(m, fn, 0, size - 7);
  drawFinder(m, fn, size - 7, 0);

  // Alignment patterns.
  const positions = ALIGN_POSITIONS[version - 1]!;
  const last = positions[positions.length - 1] ?? 0;
  for (const r of positions) {
    for (const c of positions) {
      if (
        (r === 6 && c === 6) ||
        (r === 6 && c === last) ||
        (r === last && c === 6)
      ) {
        continue;
      }
      drawAlignment(m, fn, r, c);
    }
  }

  // Dark module.
  setFn(m, fn, size - 8, 8, true);

  // Reserve format info areas (filled later, but must be marked as function).
  reserveFormat(fn, size);

  // Version info (v >= 7).
  if (version >= 7) drawVersion(m, fn, version, size);
}

function drawFinder(
  m: boolean[][],
  fn: boolean[][],
  row: number,
  col: number
): void {
  for (let dr = -1; dr <= 7; dr++) {
    for (let dc = -1; dc <= 7; dc++) {
      const r = row + dr;
      const c = col + dc;
      if (r < 0 || c < 0 || r >= m.length || c >= m.length) continue;
      const inRing =
        dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6
          ? dr === 0 || dr === 6 || dc === 0 || dc === 6
          : false;
      const inCenter = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
      setFn(m, fn, r, c, inRing || inCenter);
    }
  }
}

function drawAlignment(
  m: boolean[][],
  fn: boolean[][],
  row: number,
  col: number
): void {
  for (let dr = -2; dr <= 2; dr++) {
    for (let dc = -2; dc <= 2; dc++) {
      const dark = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
      setFn(m, fn, row + dr, col + dc, dark);
    }
  }
}

function reserveFormat(fn: boolean[][], size: number): void {
  for (let i = 0; i <= 8; i++) {
    fn[8]![i] = true;
    fn[i]![8] = true;
  }
  for (let i = 0; i < 8; i++) {
    fn[size - 1 - i]![8] = true;
    fn[8]![size - 1 - i] = true;
  }
}

function drawVersion(
  m: boolean[][],
  fn: boolean[][],
  version: number,
  size: number
): void {
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >> 11) * 0x1f25);
  const bits = (version << 12) | rem;
  for (let i = 0; i < 18; i++) {
    const bit = ((bits >> i) & 1) === 1;
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFn(m, fn, b, a, bit);
    setFn(m, fn, a, b, bit);
  }
}

function drawCodewords(
  m: boolean[][],
  fn: boolean[][],
  data: number[],
  size: number
): void {
  let i = 0;
  const totalBits = data.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const col = right - j;
        const upward = ((right + 1) & 2) === 0;
        const row = upward ? size - 1 - vert : vert;
        if (!fn[row]![col] && i < totalBits) {
          const bit = (data[i >> 3]! >> (7 - (i & 7))) & 1;
          m[row]![col] = bit === 1;
          i++;
        }
      }
    }
  }
}

function maskCondition(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
  }
}

function applyMask(
  m: boolean[][],
  fn: boolean[][],
  mask: number,
  size: number
): void {
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (!fn[row]![col] && maskCondition(mask, row, col)) {
        m[row]![col] = !m[row]![col];
      }
    }
  }
}

function chooseBestMask(
  m: boolean[][],
  fn: boolean[][],
  ecLevel: EcLevel,
  size: number
): number {
  let bestMask = 0;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(m, fn, mask, size);
    drawFormatBits(m, ecLevel, mask, size);
    const score = penalty(m, size);
    if (score < bestScore) {
      bestScore = score;
      bestMask = mask;
    }
    applyMask(m, fn, mask, size); // revert data mask (format overwritten next)
  }
  return bestMask;
}

function drawFormatBits(
  m: boolean[][],
  ecLevel: EcLevel,
  mask: number,
  size: number
): void {
  const ecIndicator = ecLevel === "L" ? 0b01 : 0b00; // M = 00
  const data = (ecIndicator << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;
  const getBit = (i: number) => ((bits >> i) & 1) === 1;

  for (let i = 0; i <= 5; i++) m[i]![8] = getBit(i);
  m[7]![8] = getBit(6);
  m[8]![8] = getBit(7);
  m[8]![7] = getBit(8);
  for (let i = 9; i < 15; i++) m[8]![14 - i] = getBit(i);

  for (let i = 0; i < 8; i++) m[8]![size - 1 - i] = getBit(i);
  for (let i = 8; i < 15; i++) m[size - 15 + i]![8] = getBit(i);
  m[size - 8]![8] = true; // dark module stays dark
}

function penalty(m: boolean[][], size: number): number {
  let score = 0;

  // Rule 1: runs of 5+ same-color modules per row/column.
  for (let r = 0; r < size; r++) {
    let runColor = m[r]![0]!;
    let runLen = 1;
    for (let c = 1; c < size; c++) {
      if (m[r]![c] === runColor) {
        runLen++;
      } else {
        if (runLen >= 5) score += runLen - 2;
        runColor = m[r]![c]!;
        runLen = 1;
      }
    }
    if (runLen >= 5) score += runLen - 2;
  }
  for (let c = 0; c < size; c++) {
    let runColor = m[0]![c]!;
    let runLen = 1;
    for (let r = 1; r < size; r++) {
      if (m[r]![c] === runColor) {
        runLen++;
      } else {
        if (runLen >= 5) score += runLen - 2;
        runColor = m[r]![c]!;
        runLen = 1;
      }
    }
    if (runLen >= 5) score += runLen - 2;
  }

  // Rule 2: 2x2 blocks of the same color.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = m[r]![c]!;
      if (v === m[r]![c + 1] && v === m[r + 1]![c] && v === m[r + 1]![c + 1]) {
        score += 3;
      }
    }
  }

  // Rule 3: finder-like patterns (1:1:3:1:1) in rows and columns.
  const pattern = [true, false, true, true, true, false, true];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size - 6; c++) {
      if (matchPattern(m, r, c, pattern, true)) score += 40;
    }
  }
  for (let c = 0; c < size; c++) {
    for (let r = 0; r < size - 6; r++) {
      if (matchPattern(m, r, c, pattern, false)) score += 40;
    }
  }

  // Rule 4: overall dark/light balance.
  let dark = 0;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) if (m[r]![c]) dark++;
  }
  const percent = (dark * 100) / (size * size);
  const k = Math.floor(Math.abs(percent - 50) / 5);
  score += k * 10;

  return score;
}

function matchPattern(
  m: boolean[][],
  r: number,
  c: number,
  pattern: boolean[],
  horizontal: boolean
): boolean {
  for (let i = 0; i < 7; i++) {
    const v = horizontal ? m[r]![c + i] : m[r + i]![c];
    if (v !== pattern[i]) return false;
  }
  // require 4 light modules on one side (approximation of the standard rule)
  return true;
}

function setFn(
  m: boolean[][],
  fn: boolean[][],
  r: number,
  c: number,
  dark: boolean
): void {
  if (r < 0 || c < 0 || r >= m.length || c >= m.length) return;
  m[r]![c] = dark;
  fn[r]![c] = true;
}

/* ------------------------------- rendering ------------------------------- */

export function renderQrSvg(
  matrix: QrMatrix,
  opts: {
    moduleSize?: number;
    margin?: number;
    dark?: string;
    light?: string;
  } = {}
): string {
  const moduleSize = opts.moduleSize ?? 4;
  const margin = opts.margin ?? 4;
  const dark = opts.dark ?? "#000000";
  const light = opts.light ?? "#ffffff";
  const { size, modules } = matrix;
  const dim = (size + margin * 2) * moduleSize;

  let path = "";
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (modules[r]![c]) {
        const x = (c + margin) * moduleSize;
        const y = (r + margin) * moduleSize;
        path += `M${x} ${y}h${moduleSize}v${moduleSize}h${-moduleSize}z`;
      }
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${dim}" height="${dim}" ` +
    `viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">` +
    `<rect width="${dim}" height="${dim}" fill="${light}"/>` +
    `<path d="${path}" fill="${dark}"/></svg>`
  );
}
